import Phaser from "phaser";
import type { StageConfig } from "../config/stages.ts";
import { bakeTexture } from "./bake.ts";
import { css, hash01, shade } from "./color.ts";

/** Visual sampling step along the surface (physics uses a coarser step; the gap between a
 * smooth curve and its 24px chords is a pixel or two, invisible under the grass band). */
const VISUAL_STEP = 6;
/** Extra width painted past each chunk edge so neighbouring chunks overlap without seams. */
export const TERRAIN_ART_PAD = 24;
/** How far below the lowest surface point the baked dirt reaches; below that a flat fill in
 * the same base colour continues (see `terrainDeepColor`). */
const DIRT_DEPTH = 460;
const TUFT_HEADROOM = 28;
const GRASS_THICKNESS = 12;

export interface TerrainChunkArt {
  key: string;
  /** World position of the texture's top-left corner. */
  x: number;
  y: number;
  width: number;
  height: number;
}

export function terrainDeepColor(stage: StageConfig): number {
  return shade(stage.groundColor, -0.1);
}

function seedNumber(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
  return h | 0;
}

/**
 * Paints one terrain chunk: shaded dirt with embedded stones/dents, a thick grass band on
 * top and grass tufts. Decorations are placed on a world-space grid with a deterministic
 * hash, so every chunk agrees on what straddles its borders.
 */
export function bakeTerrainChunk(
  scene: Phaser.Scene,
  stage: StageConfig,
  heightAt: (x: number) => number,
  startX: number,
  endX: number,
): TerrainChunkArt {
  const x0 = startX - TERRAIN_ART_PAD;
  const x1 = endX + TERRAIN_ART_PAD;
  const surfaceY = (x: number) => -heightAt(x);

  const points: { x: number; y: number }[] = [];
  for (let x = x0; x <= x1 + 0.01; x += VISUAL_STEP) points.push({ x, y: surfaceY(x) });

  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }
  const top = Math.floor(minY - TUFT_HEADROOM);
  const width = x1 - x0;
  const height = Math.ceil(maxY + DIRT_DEPTH - top);
  const key = `terrain-${stage.id}-${startX}`;
  if (scene.textures.exists(key)) scene.textures.remove(key);

  const seed = seedNumber(stage.seed);
  const dirt = stage.groundColor;
  const grass = stage.surfaceColor;

  bakeTexture(
    scene,
    key,
    width,
    height,
    (ctx) => {
      ctx.translate(-x0, -top);

      const surfacePath = () => {
        ctx.beginPath();
        points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      };
      const groundPath = () => {
        surfacePath();
        ctx.lineTo(x1, top + height);
        ctx.lineTo(x0, top + height);
        ctx.closePath();
      };

      // Dirt: base colour, lighter toward the surface (stacked translucent strokes along the
      // surface act as a gradient that follows the terrain's shape).
      groundPath();
      ctx.fillStyle = css(terrainDeepColor(stage));
      ctx.fill();

      ctx.save();
      groundPath();
      ctx.clip();
      ctx.strokeStyle = css(shade(dirt, 0.1), 0.25);
      for (const w of [260, 170, 100, 50]) {
        ctx.lineWidth = w;
        surfacePath();
        ctx.stroke();
      }

      drawPebbles(ctx, x0, x1, surfaceY, seed, dirt);
      drawDents(ctx, x0, x1, surfaceY, seed, dirt);
      ctx.restore();

      // Grass band: a dark lip under a bright band, with a light top edge.
      const band = (from: number, to: number) => {
        ctx.beginPath();
        points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y + from) : ctx.lineTo(p.x, p.y + from)));
        for (let i = points.length - 1; i >= 0; i--) {
          const p = points[i];
          if (p) ctx.lineTo(p.x, p.y + to);
        }
        ctx.closePath();
      };
      band(0, GRASS_THICKNESS + 5);
      ctx.fillStyle = css(shade(grass, -0.35));
      ctx.fill();
      band(0, GRASS_THICKNESS);
      ctx.fillStyle = css(grass);
      ctx.fill();
      band(0, 4);
      ctx.fillStyle = css(shade(grass, 0.22));
      ctx.fill();

      drawTufts(ctx, x0, x1, surfaceY, seed, grass);
    },
    1,
  );

  return { key, x: x0, y: top, width, height };
}

function drawDents(
  ctx: CanvasRenderingContext2D,
  x0: number,
  x1: number,
  surfaceY: (x: number) => number,
  seed: number,
  dirt: number,
): void {
  const cell = 70;
  const maxR = 34;
  for (let k = Math.floor((x0 - maxR) / cell); k <= Math.ceil((x1 + maxR) / cell); k++) {
    if (hash01(k, seed) < 0.2) continue;
    const r = 10 + hash01(k, seed + 3) * 24;
    const cx = k * cell + hash01(k, seed + 1) * cell;
    const depth = GRASS_THICKNESS + 14 + r + hash01(k, seed + 2) * 300;
    const cy = surfaceY(cx) + depth;

    // A recessed dent: darker fill, shadowed upper rim, light catching the lower rim.
    ctx.fillStyle = css(shade(dirt, -0.12));
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineWidth = Math.max(2, r * 0.14);
    ctx.strokeStyle = css(shade(dirt, -0.35), 0.55);
    ctx.beginPath();
    ctx.arc(cx, cy, r - ctx.lineWidth / 2, Math.PI * 1.05, Math.PI * 1.95);
    ctx.stroke();
    ctx.strokeStyle = css(shade(dirt, 0.18), 0.75);
    ctx.beginPath();
    ctx.arc(cx, cy, r - ctx.lineWidth / 2, Math.PI * 0.1, Math.PI * 0.9);
    ctx.stroke();
  }
}

function drawPebbles(
  ctx: CanvasRenderingContext2D,
  x0: number,
  x1: number,
  surfaceY: (x: number) => number,
  seed: number,
  dirt: number,
): void {
  const cell = 26;
  ctx.fillStyle = css(shade(dirt, -0.2), 0.45);
  for (let k = Math.floor((x0 - 6) / cell); k <= Math.ceil((x1 + 6) / cell); k++) {
    if (hash01(k, seed + 7) < 0.35) continue;
    const cx = k * cell + hash01(k, seed + 8) * cell;
    const cy = surfaceY(cx) + GRASS_THICKNESS + 10 + hash01(k, seed + 9) * 380;
    ctx.beginPath();
    ctx.ellipse(cx, cy, 2 + hash01(k, seed + 10) * 3, 1.5 + hash01(k, seed + 11) * 2, 0, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawTufts(
  ctx: CanvasRenderingContext2D,
  x0: number,
  x1: number,
  surfaceY: (x: number) => number,
  seed: number,
  grass: number,
): void {
  const cell = 85;
  for (let k = Math.floor((x0 - 20) / cell); k <= Math.ceil((x1 + 20) / cell); k++) {
    if (hash01(k, seed + 20) < 0.35) continue;
    const cx = k * cell + hash01(k, seed + 21) * cell;
    const base = surfaceY(cx) + 4;
    const blades = 3 + Math.floor(hash01(k, seed + 22) * 3);
    const tall = 9 + hash01(k, seed + 23) * 10;
    ctx.fillStyle = css(shade(grass, 0.12));
    for (let i = 0; i < blades; i++) {
      const t = blades === 1 ? 0 : i / (blades - 1) - 0.5;
      const bx = cx + t * 12;
      const lean = t * 9 + (hash01(k * 7 + i, seed + 24) - 0.5) * 4;
      const bh = tall * (1 - Math.abs(t) * 0.6);
      ctx.beginPath();
      ctx.moveTo(bx - 2.6, base);
      ctx.quadraticCurveTo(bx + lean * 0.3, base - bh * 0.6, bx + lean, base - bh);
      ctx.quadraticCurveTo(bx + lean * 0.4, base - bh * 0.5, bx + 2.6, base);
      ctx.closePath();
      ctx.fill();
    }
  }
}
